import { createContext, useContext, useId, type ReactNode } from 'react';
import './FormField.css';

/**
 * FormField — 모든 입력의 껍데기 (디자인 가이드 02-components.md B1).
 *
 * 지금까지 라벨·필수 표시·도움말·오류 문구를 **132개 파일이 각자** 그렸다.
 * 그래서 같은 폼인데 화면마다 라벨 굵기도 오류 색도 다르고, 무엇보다
 * `aria-describedby` 를 이어 준 자리가 거의 없었다.
 *
 * ★★ **접근성 배선을 «컨텍스트로 내려 준다».**
 *    `cloneElement` 로 자식 props 를 덮어쓰는 방식은 자식이 무엇이든 될 수 있어 깨지기 쉽다.
 *    대신 컨트롤이 `useFormField()` 로 **자기가 필요한 것만 가져간다** —
 *    id · aria-describedby(도움말 + 오류 **둘 다**) · aria-invalid · aria-required · disabled.
 *    `Input` 은 이미 그렇게 하고 있으니 호출부는 아무것도 안 이어도 된다.
 *
 * ★ **오류가 뜨면 컨트롤 테두리도 함께 붉어진다** — 문구만 바뀌면 색각 이상 사용자가 못 본다.
 *   그 판정은 `invalid` 로 컨텍스트를 타고 내려간다(FormField.css 는 문구만 그린다).
 *
 * ★ 필수 표시는 `*` 한 글자라 읽어 주지 못한다 → 옆에 화면에 안 보이는 「(필수)」를 같이 둔다.
 */

export interface FormFieldContextValue {
  /** 라벨의 htmlFor 와 짝이 되는 컨트롤 id. */
  controlId: string;
  /** 도움말 + 오류 id 를 공백으로 이은 것. 없으면 undefined. */
  describedBy?: string;
  invalid: boolean;
  required: boolean;
  disabled: boolean;
}

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

/** 컨트롤이 제 배선을 가져가는 훅. FormField 밖이면 null. */
export const useFormField = (): FormFieldContextValue | null => useContext(FormFieldContext);

export type FormFieldLayout = 'stack' | 'side';

export interface FormFieldProps {
  label?: ReactNode;
  /** 컨트롤 id 를 직접 정할 때. 안 주면 useId 로 만든다. */
  htmlFor?: string;
  required?: boolean;
  /** 라벨 아래 보조 설명. 「원래 그런 규칙」이면 화면이 아니라 도움말로 (03-patterns). */
  hint?: ReactNode;
  /** 있으면 오류 상태. 컨트롤 테두리도 함께 붉어진다. */
  error?: ReactNode;
  /** 글자수 등 — 숫자 짝을 주면 `n/max` 로 그리고, 넘으면 붉어진다. */
  counter?: ReactNode | { value: number; max: number };
  layout?: FormFieldLayout;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}

export function FormField({
  label,
  htmlFor,
  required = false,
  hint,
  error,
  counter,
  layout = 'stack',
  disabled = false,
  children,
  className,
}: FormFieldProps) {
  const uid = useId();
  const controlId = htmlFor ?? `${uid}-control`;
  const hintId = `${uid}-hint`;
  const errorId = `${uid}-error`;
  const invalid = error != null && error !== false && error !== '';

  const describedBy = [hint ? hintId : null, invalid ? errorId : null].filter(Boolean).join(' ') || undefined;

  const isCounterPair =
    counter != null && typeof counter === 'object' && 'value' in (counter as object) && 'max' in (counter as object);
  const pair = isCounterPair ? (counter as { value: number; max: number }) : null;
  const counterNode: ReactNode = pair ? `${pair.value}/${pair.max}` : (counter as ReactNode);
  const counterOver = pair != null && pair.value > pair.max;

  const cls = [
    'wc-field',
    `wc-field--${layout}`,
    invalid ? 'wc-field--invalid' : '',
    disabled ? 'wc-field--disabled' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <FormFieldContext.Provider value={{ controlId, describedBy, invalid, required, disabled }}>
      <div className={cls}>
        {label != null && (
          <label className="wc-field__label" htmlFor={controlId}>
            {label}
            {required && (
              <>
                <span className="wc-field__required" aria-hidden>*</span>
                <span className="wc-field__sr">(필수)</span>
              </>
            )}
          </label>
        )}
        <div className="wc-field__body">
          {hint != null && (
            <p className="wc-field__hint" id={hintId}>{hint}</p>
          )}
          <div className="wc-field__control">{children}</div>
          {(invalid || counterNode != null) && (
            <div className="wc-field__foot">
              {invalid ? (
                <p className="wc-field__error" id={errorId} role="alert">{error}</p>
              ) : (
                <span />
              )}
              {counterNode != null && (
                <span className={['wc-field__counter', counterOver ? 'is-over' : ''].filter(Boolean).join(' ')}>
                  {counterNode}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </FormFieldContext.Provider>
  );
}
